import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../../app/Spinner';

export interface WaybillPreviewPanelProps {
  html: string;
  isLoading: boolean;
  error: string | null;
  hasLogo: boolean;
}

export function WaybillPreviewPanel({
  html,
  isLoading,
  error,
  hasLogo,
}: WaybillPreviewPanelProps) {
  const { t } = useTranslation(['m-waybill']);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState(900);

  const previewSrc = useMemo(() => {
    if (!html) return undefined;
    return URL.createObjectURL(new Blob([html], { type: 'text/html;charset=utf-8' }));
  }, [html]);

  const syncIframeHeight = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    const height = Math.max(
      doc.documentElement.scrollHeight,
      doc.body.scrollHeight,
      doc.documentElement.offsetHeight,
      doc.body.offsetHeight,
    );
    setIframeHeight(height + 16);
  }, []);

  useEffect(() => {
    return () => {
      if (previewSrc) URL.revokeObjectURL(previewSrc);
    };
  }, [previewSrc]);

  useEffect(() => {
    setIframeHeight(900);
  }, [previewSrc]);

  return (
    <div className="flex flex-col gap-3 min-h-[640px] lg:sticky lg:top-4">
      <h3 className="text-sm font-semibold text-fg">{t('m-waybill:preview.title')}</h3>
      {!hasLogo ? (
        <p className="text-xs text-muted-foreground">{t('m-waybill:preview.no_logo_hint')}</p>
      ) : null}
      <div className="relative flex-1 rounded-xl border border-border bg-white overflow-auto min-h-[600px] max-h-[min(90vh,920px)]">
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-background/60 z-10">
            <Spinner />
            <span className="ms-2 text-sm text-muted-foreground">
              {t('m-waybill:preview.loading')}
            </span>
          </div>
        ) : null}
        {error ? (
          <div className="p-4 text-sm text-destructive">{t('m-waybill:preview.error')}</div>
        ) : null}
        {!error && previewSrc ? (
          <iframe
            ref={iframeRef}
            title={t('m-waybill:preview.title')}
            src={previewSrc}
            onLoad={syncIframeHeight}
            className="block w-full border-0 bg-white"
            style={{ height: iframeHeight }}
          />
        ) : null}
      </div>
    </div>
  );
}
